import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
    fetchTransformations,
    type TransformTypeDefinition,
    type DynamicField,
} from '@/features/adp/api/transformations.api'
 
// ─── Constants ────────────────────────────────────────────────────────────────
 
/**
* Maps the connectorId used in the UI/routing to the API's `name` field.
* Extend this map as new transformation connectors are added.
*/
export const TRANSFORMATION_CONNECTOR_TO_API_NAME: Record<string, string> = {
    split: 'split',
    datesplit: 'date Split',
    findreplace: 'find and replace',
    pivot: 'concat',
    edit: 'rename columns',
    datetime: 'date to integer',
    filter: 'filter',
    sort: 'sort',
    dropfields: 'drop fields',
    cumulative: 'cumulative',
}
 
// ─── Types ────────────────────────────────────────────────────────────────────
 
export type UseTransformationsResult = {
    /** The matched transform definition for the given connectorId, or null if not found / still loading. */
    selectedTransformType: TransformTypeDefinition | null
    /** All fields from the matched transform's schema definition. Empty array if none. */
    fields: DynamicField[]
    /** Whether the API request is in-flight. */
    isLoading: boolean
    /** Error message string if the fetch failed, otherwise null. */
    error: string | null
}
 
// ─── Hook ─────────────────────────────────────────────────────────────────────
 
/**
* Optional reverse map of API transformation names to UI connector ids.
* Keep this in sync with `TRANSFORMATION_CONNECTOR_TO_API_NAME`.
*/
const TRANSFORMATION_API_NAME_TO_CONNECTOR: Record<string, string> = Object.entries(
    TRANSFORMATION_CONNECTOR_TO_API_NAME
).reduce((acc, [connectorId, apiName]) => {
    acc[apiName.trim().toLowerCase()] = connectorId
    return acc
}, {} as Record<string, string>)
 
function normalizeTransformationKey(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
}
 
function findTransformTypeByConnector(
    transformTypes: TransformTypeDefinition[],
    connectorId: string
): TransformTypeDefinition | null {
    const normalizedConnector = normalizeTransformationKey(connectorId)

    const expectedApiName =
        TRANSFORMATION_CONNECTOR_TO_API_NAME[connectorId.trim().toLowerCase()] ??
        TRANSFORMATION_API_NAME_TO_CONNECTOR[connectorId.trim().toLowerCase()]

    return (
        transformTypes.find(item => {
            const normalizedName = normalizeTransformationKey(item.name ?? '')

            if (expectedApiName) {
                // Prefer exact matches first
                if (normalizedName === normalizeTransformationKey(expectedApiName)) {
                    return true
                }
                // For cases like "date Split" vs "split", use exact case-sensitive match
                if (item.name?.trim().toLowerCase() === expectedApiName.trim().toLowerCase()) {
                    return true
                }
                // Fallback to includes only if no exact match
                return normalizedName.includes(normalizeTransformationKey(expectedApiName)) ||
                       normalizedConnector === normalizeTransformationKey(item.name)
            }

            return (
                normalizedName === normalizedConnector ||
                normalizedName.includes(normalizedConnector) ||
                normalizedConnector.includes(normalizedName)
            )
        }) ?? null
    )
}
 
export function useAllTransformations() {
    const { data = [], isLoading, error } = useQuery<TransformTypeDefinition[], Error>({
        queryKey: ['adp', 'transformations'],
        queryFn: () => fetchTransformations(),
        staleTime: 60_000,
    })
 
    return {
        transformations: Array.isArray(data) ? data : [],
        isLoading,
        error: error?.message ?? null,
    }
}
 
/**
* Fetches all transformation types from the API and resolves the one
* matching the given `connectorId` via `TRANSFORMATION_CONNECTOR_TO_API_NAME`.
*
* Mirrors the pattern used in `useAdpPaletteDataSources`.
*/
export function useTransformations(
    connectorId: string | undefined
): UseTransformationsResult {
    const { transformations, isLoading, error } = useAllTransformations()
 
    const selectedTransformType = useMemo(() => {
        if (!connectorId || !transformations.length) return null
 
        return findTransformTypeByConnector(transformations, connectorId)
    }, [transformations, connectorId])
 
    const fields = selectedTransformType?.schema_definition?.fields ?? []
 
    return {
        selectedTransformType,
        fields,
        isLoading,
        error,
    }
}
 
// ─── Helpers exported for use in ConfigureTransformationSheet ─────────────────
 
/**
* Builds an empty form state from a list of DynamicFields.
* Multiselect fields default to `[]`, everything else to `''`.
*/
export function buildInitialFormState(
    fields: DynamicField[]
): Record<string, string | string[]> {
    return Object.fromEntries(
        fields.map(field => [field.key, field.type === 'multiselect' ? [] : ''])
    )
}